import React, { Component } from 'react'
import { cloneDeep } from 'lodash'

import {
  filterDataFlow,
  getSectionCompletionState,
  inputValidator,
  setAutoPopulatedFieldsFromText,
  setAutoPopulatedFieldsFromSelector,
} from '../../utils/workflowUtils'
import DataFlowNavigator from '../Navigation/DataFlowNavigator'
import DataFlowSummary from '../DataFlows/DataFlowSummary'
import { toUpperCaseCustom } from '../../utils'
import { ISectionConfigItem } from '../../interfaces'
import { FieldConfigItemSchema } from '../../types'

interface DataFlowManagerProps {
  fieldConfig: Record<string, Record<string, any>>
  sectionConfig: ISectionConfigItem[]
  dataFlow: Record<string, Record<string, any>[]>
  data: Record<string, any>
  showSectionHeader: boolean
  highlightEmpty: boolean
  dataFlowDisabled: boolean
}

interface DataFlowManagerState {
  showSectionHeader: boolean
  showForm: boolean
  data: Record<string, any>
  sectionCompletionState: Record<string, boolean>
  dataFlowFiltered: Record<string, FieldConfigItemSchema[]>
  selectedSectionFlowFiltered: FieldConfigItemSchema[]
  dataFlowFilters: Record<string, any>
  currentSectionName: string
  currentSectionLabel: string
  fieldWarnings: Record<string, string>
}

class DataFlowManager extends Component<DataFlowManagerProps, DataFlowManagerState> {
  dataFlowNavigatorMenuRef: React.RefObject<DataFlowNavigator>

  constructor(props: DataFlowManagerProps) {
    super(props)
    this.dataFlowNavigatorMenuRef = React.createRef()
  }

  state: DataFlowManagerState = {
    showSectionHeader: false,
    showForm: false,
    data: {},
    sectionCompletionState: {},
    dataFlowFiltered: {},
    selectedSectionFlowFiltered: [],
    dataFlowFilters: {},
    currentSectionName: '',
    currentSectionLabel: '',
    fieldWarnings: {},
  }

  componentDidMount() {
    this.dataFlowNavigatorMenuRef = React.createRef()
    const { dataFlow = {}, data = {}, showSectionHeader = false, sectionConfig = [] } = this.props

    if (sectionConfig.length) {
      const currentSectionName = sectionConfig[0].sectionName
      const [dataFlowFiltered, dataFlowFilters] = filterDataFlow(dataFlow, data)
      const sectionCompletionState = getSectionCompletionState(dataFlowFiltered, data)

      this.setState({
        data: cloneDeep(data),
        showSectionHeader,
        sectionCompletionState,
        dataFlowFiltered,
        dataFlowFilters,
        selectedSectionFlowFiltered: dataFlowFiltered[currentSectionName],
        currentSectionName,
        currentSectionLabel: sectionConfig[0].sectionLabel,
        showForm: true,
      })
    }
  }

  getCapturedData = () => {
    return this.state.data
  }

  goToSubmenu = (nextSectionName: string) => {
    const { dataFlowFiltered, data } = this.state
    const sectionCompletionState = getSectionCompletionState(dataFlowFiltered, data)

    this.setState({
      selectedSectionFlowFiltered: dataFlowFiltered[nextSectionName],
      sectionCompletionState,
      currentSectionName: nextSectionName,
    })
  }

  remountForm(isPriField: boolean) {
    if (isPriField) {
      this.setState({ showForm: false }, () => this.setState({ showForm: true }))
    }
  }

  textHandler = (
    text: string,
    key: string,
    validationType: string | undefined,
    compulsory: boolean,
    autoPopulate: Record<string, Record<string, any>> = {},
    isPriField: boolean,
  ) => {
    let warning = ''
    if (validationType !== undefined) {
      warning = inputValidator(compulsory, validationType, text).warning
    }
    console.log('[AUTOPOP-DIAG] textHandler', { key, text, autoPopulateKeys: Object.keys(autoPopulate) })
    const autoPopulated = setAutoPopulatedFieldsFromText(
      text,
      { ...this.state.data, [key]: toUpperCaseCustom(text) },
      autoPopulate,
    )
    const fieldWarnings = { ...this.state.fieldWarnings, [key]: warning }
    this.setState({ data: autoPopulated.data, fieldWarnings }, () =>
      this.remountForm(isPriField && autoPopulated.applied),
    )
  }

  selectionHandler = (
    values: string[],
    key: string,
    workflowBranch: boolean,
    autoPopulate: Record<string, Record<string, any>> = {},
    isPriField: boolean,
  ) => {
    const { dataFlow } = this.props
    let { data, dataFlowFiltered, dataFlowFilters, currentSectionName } = this.state
    let selectedSectionFlowFiltered = dataFlowFiltered[currentSectionName]
    data[key] = values
    const autoPopResult = setAutoPopulatedFieldsFromSelector(values, cloneDeep(data), autoPopulate)
    console.log('[AUTOPOP-DIAG] selectionHandler', {
      key,
      values,
      autoPopulateKeys: Object.keys(autoPopulate),
      applied: autoPopResult.applied,
      changedFields: Object.keys(autoPopResult.data).filter((k) => autoPopResult.data[k] !== data[k]),
    })
    data = autoPopResult.data
    if (workflowBranch) {
      const filterResult = filterDataFlow(dataFlow, data)
      dataFlowFiltered = filterResult[0]
      dataFlowFilters = filterResult[1]
      selectedSectionFlowFiltered = dataFlowFiltered[currentSectionName]
    }
    this.setState({ data, dataFlowFiltered, dataFlowFilters, selectedSectionFlowFiltered }, () =>
      this.remountForm(isPriField),
    )
  }

  render() {
    const {
      data,
      showSectionHeader,
      currentSectionName,
      selectedSectionFlowFiltered,
      currentSectionLabel,
      fieldWarnings,
      showForm,
    } = this.state

    const { sectionConfig, fieldConfig, highlightEmpty, dataFlowDisabled } = this.props

    let sectionMenu = null
    if (showSectionHeader) {
      sectionMenu = (
        <DataFlowNavigator
          ref={this.dataFlowNavigatorMenuRef}
          sectionConfig={sectionConfig}
          currentSectionName={currentSectionName}
          onSectionClick={(sectionName) => this.goToSubmenu(sectionName)}
        />
      )
    }
    let form = null
    if (showForm) {
      form = (
        <DataFlowSummary
          style={{ marginTop: showSectionHeader ? 0 : 35 }}
          data={data}
          fieldConfig={fieldConfig}
          sectionLabel={currentSectionLabel}
          sectionFlow={selectedSectionFlowFiltered}
          fieldWarnings={fieldWarnings}
          highlightEmpty={highlightEmpty}
          dataFlowDisabled={dataFlowDisabled}
          verifiedFields={data.verifiedFields ? data.verifiedFields : {}}
          textHandler={(
            text: string,
            key: string,
            validationType: string | undefined,
            compulsory: boolean,
            autoPopulate: Record<string, Record<string, any>> = {},
            isPriField: boolean,
          ) => this.textHandler(text, key, validationType, compulsory, autoPopulate, isPriField)}
          selectionHandler={(
            values: string[],
            key: string,
            workflowBranch: boolean,
            autoPopulate: Record<string, Record<string, any>>,
            isPriField: boolean,
          ) => this.selectionHandler(values, key, workflowBranch, autoPopulate, isPriField)}
        />
      )
    }

    return (
      <div>
        {sectionMenu}
        {form}
      </div>
    )
  }
}

export default DataFlowManager
